/**
 * NZ week helpers for student meal plan weekly booking.
 */

export const MEAL_PLAN_TZ = 'Pacific/Auckland';
export const MEAL_PERIODS = ['breakfast', 'lunch', 'dinner'] as const;

/** Meals switched on for one week. An empty or missing list means all three. */
export function offeredPeriods(periods: readonly string[] | undefined | null): Array<(typeof MEAL_PERIODS)[number]> {
  if (!periods || periods.length === 0) return [...MEAL_PERIODS];
  const chosen = new Set(periods);
  const next = MEAL_PERIODS.filter((period) => chosen.has(period));
  return next.length ? [...next] : [...MEAL_PERIODS];
}

/** Format instant as YYYY-MM-DD in NZ. */
export function nzDateKey(ms: number = Date.now()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MEAL_PLAN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms));
}

/** ISO weekday 1=Mon … 7=Sun for an NZ calendar date key. */
export function isoWeekdayFromDayKey(dayKey: string): number {
  const [y, m, d] = dayKey.split('-').map(Number);
  // Use noon UTC to avoid DST edge when interpreting civil date
  const utc = Date.UTC(y, m - 1, d, 12, 0, 0);
  const wd = new Date(utc).getUTCDay(); // 0=Sun
  return wd === 0 ? 7 : wd;
}

/** Monday YYYY-MM-DD for the NZ week containing `ms`. */
export function weekIdContaining(ms: number = Date.now()): string {
  const today = nzDateKey(ms);
  const wd = isoWeekdayFromDayKey(today);
  return addDaysToDayKey(today, 1 - wd);
}

/** Next week’s Monday after the week that contains `ms`. */
export function nextWeekId(ms: number = Date.now()): string {
  return addDaysToDayKey(weekIdContaining(ms), 7);
}

export function addDaysToDayKey(dayKey: string, delta: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + delta, 12, 0, 0));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Seven day keys Mon…Sun for a weekId (Monday). */
export function dayKeysForWeek(weekId: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysToDayKey(weekId, i));
}

/**
 * Approximate NZ midnight → epoch for civil date (sufficient for week bounds / cutoff).
 * Uses fixed offset probe via formatting.
 */
export function nzMidnightMs(dayKey: string): number {
  // Binary-ish search: find UTC ms whose NZ date is dayKey and hour is 0
  const [y, m, d] = dayKey.split('-').map(Number);
  let guess = Date.UTC(y, m - 1, d, 12, 0, 0);
  for (let i = 0; i < 48; i++) {
    const key = nzDateKey(guess);
    if (key === dayKey) {
      const hour = Number(
        new Intl.DateTimeFormat('en-US', {
          timeZone: MEAL_PLAN_TZ,
          hour: 'numeric',
          hour12: false,
        }).format(new Date(guess)),
      );
      if (hour === 0 || hour === 24) return guess - (guess % 60000);
      guess -= hour * 3600000;
    } else if (key > dayKey) {
      guess -= 3600000;
    } else {
      guess += 3600000;
    }
  }
  return Date.UTC(y, m - 1, d, 12, 0, 0) - 12 * 3600000;
}

export function weekBoundsMs(weekId: string): { weekStartMs: number; weekEndMs: number } {
  const weekStartMs = nzMidnightMs(weekId);
  const nextMon = addDaysToDayKey(weekId, 7);
  const weekEndMs = nzMidnightMs(nextMon) - 1;
  return { weekStartMs, weekEndMs };
}

/**
 * Saturday 23:59:59.999 NZ of the week *before* `targetWeekId`
 * = cancellation cutoff for bookings of `targetWeekId`.
 * targetWeekId is next week's Monday → cutoff is Saturday of current week.
 */
export function cancellationCutoffMs(targetWeekId: string): number {
  const saturday = addDaysToDayKey(targetWeekId, -2); // week starts Mon; Sat is -2
  const nextDay = addDaysToDayKey(saturday, 1);
  return nzMidnightMs(nextDay) - 1;
}

export function isBeforeCancellationCutoff(targetWeekId: string, now = Date.now()): boolean {
  return now <= cancellationCutoffMs(targetWeekId);
}

export function emptyDayMenu(): {
  title: string;
  breakfastText: string;
  lunchText: string;
  dinnerText: string;
  breakfast: [];
  lunch: [];
  dinner: [];
} {
  return {
    title: '',
    breakfastText: '',
    lunchText: '',
    dinnerText: '',
    breakfast: [],
    lunch: [],
    dinner: [],
  };
}

type DayMenuLike = {
  title?: string;
  breakfastText?: string;
  lunchText?: string;
  dinnerText?: string;
  breakfast?: { name?: string }[];
  lunch?: { name?: string }[];
  dinner?: { name?: string }[];
};

/** Written meal, or the older dish names joined, with no prices. */
export function mealPeriodText(day: DayMenuLike | undefined, period: 'breakfast' | 'lunch' | 'dinner'): string {
  const writtenKey = `${period}Text` as 'breakfastText' | 'lunchText' | 'dinnerText';
  const written = day?.[writtenKey];
  if (typeof written === 'string' && written.trim()) return written.trim();
  return (day?.[period] || [])
    .map((item) => (item.name || '').trim())
    .filter(Boolean)
    .join(', ');
}

/** Save shape: day name and meal text only. Dish links are cleared so menu prices cannot attach. */
export function dayMenuAsText(day: DayMenuLike | undefined): {
  title: string;
  breakfastText: string;
  lunchText: string;
  dinnerText: string;
  breakfast: [];
  lunch: [];
  dinner: [];
} {
  return {
    title: (day?.title || '').trim(),
    breakfastText: mealPeriodText(day, 'breakfast'),
    lunchText: mealPeriodText(day, 'lunch'),
    dinnerText: mealPeriodText(day, 'dinner'),
    breakfast: [],
    lunch: [],
    dinner: [],
  };
}

export function emptyWeekMenu(weekId: string): Record<string, ReturnType<typeof emptyDayMenu>> {
  const menu: Record<string, ReturnType<typeof emptyDayMenu>> = {};
  for (const day of dayKeysForWeek(weekId)) {
    menu[day] = emptyDayMenu();
  }
  return menu;
}

export function bookingDocId(customerId: string, dayKey: string, period: string): string {
  return `wb-${customerId}-${dayKey}-${period}`;
}
